import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { LazyIconInfo } from '@/design-system/atoms/Icons/IconInfo/Lazy';
import { StarRatings } from '@/design-system/atoms/StarRatings';
import { Text } from '@/design-system/atoms/Text';
import { Title } from '@/design-system/atoms/Title';
import { Tooltip } from '@/design-system/atoms/Tooltip';
import { trackInteraction } from '@/utils/analytics';

import styles from './BioReviews.module.scss';
import { Review } from './components/Review';
import { Toolbar } from './components/Toolbar';

const BioReviews = ({
    title,
    htmlId,
    toolTipContent,
    overallRating,
    numberOfRate,
    reviews,
    ...props
}) => {
    const ITEMS_PER_PAGE = 5;
    const loadMoreRef = useRef();
    const dropdownRef = useRef();
    const [sortBy, setSortBy] = useState('recency');
    const [currentPage, setCurrentPage] = useState(1);
    const [currentReviews, setCurrentReviews] = useState(reviews.slice(0, ITEMS_PER_PAGE));
    const [firstNewReviewIndex, setFirstNewReviewIndex] = useState(0);
    const firstNewReviewRef = useRef(null);

    const [reviewsSortedByRating, reviewsSortedByRecency] = useMemo(() => {
        const reviewsSortedByRating = reviews?.toSorted(
            (obj1, obj2) => obj2.overallRating?.value - obj1.overallRating?.value,
        );
        const reviewsSortedByRecency = reviews?.toSorted(
            (obj1, obj2) => obj2.mentionTime - obj1.mentionTime,
        );
        return [reviewsSortedByRating, reviewsSortedByRecency];
    }, [reviews]);

    const handleSelectionChange = useCallback(
        (newKey) => {
            if (newKey !== sortBy) {
                setSortBy(newKey);
                setCurrentPage(1);
                setFirstNewReviewIndex(0);
                //analytics
                trackInteraction({
                    componentName: 'Fad Bio Reviews',
                    componentTitle: title || '',
                    actionLabel: newKey,
                    interactionType: 'fad_bio_review_sort',
                    selector: 'sort',
                    providerRating: overallRating || '',
                });
            }
        },
        [setCurrentPage, sortBy, title, overallRating],
    );

    const handleLoadMoreClick = useCallback(() => {
        setFirstNewReviewIndex(currentPage * ITEMS_PER_PAGE);
        setCurrentPage(currentPage + 1);
    }, [currentPage]);

    useEffect(() => {
        const current =
            sortBy === 'recency'
                ? reviewsSortedByRecency?.slice(0, ITEMS_PER_PAGE * currentPage)
                : reviewsSortedByRating?.slice(0, ITEMS_PER_PAGE * currentPage);

        setCurrentReviews(current);
    }, [currentPage, sortBy, reviewsSortedByRecency, reviewsSortedByRating]);

    useEffect(() => {
        const updateTabindex = (event) => {
            event.target.setAttribute('tabindex', '-1');
        };
        let firstNew;
        // Scroll down after a brief delay when loading more results
        const timer = setTimeout(() => {
            if (firstNewReviewIndex > 0 && firstNewReviewRef.current) {
                const elementRect = firstNewReviewRef.current.getBoundingClientRect();
                const absoluteElementTop = elementRect.top + window.scrollY;

                firstNewReviewRef.current.focus();

                // Scroll to the first new review
                window.scrollTo({
                    top: absoluteElementTop,
                    behavior: 'smooth',
                });
                firstNew = firstNewReviewRef.current;
                firstNewReviewRef.current.addEventListener('blur', updateTabindex);
            }
        }, 200);

        return () => {
            firstNew?.removeEventListener('blur', updateTabindex);
            clearTimeout(timer);
        };
    }, [firstNewReviewIndex]);

    return (
        <section className={styles[`bio-reviews`]} {...props} id={htmlId}>
            <ContentContainer className={styles[`bio-reviews__container`]}>
                <Grid col={{ sm: 4, md: 6, lg: 12 }}>
                    <GridColumn
                        colSpan={{ sm: 4, md: 6, lg: 12 }}
                        className={styles[`bio-reviews__header`]}
                    >
                        {title && (
                            <Title
                                className={styles[`bio-reviews__title`]}
                                variant={Title.VARIANT.H3}
                                tag={Title.TAG.H2}
                                content={title}
                            />
                        )}
                        {toolTipContent && (
                            <Tooltip
                                iconComponent={LazyIconInfo}
                                trigger={'click'}
                                buttonArialLabel={'More information'}
                                tooltipContent={toolTipContent}
                            />
                        )}
                    </GridColumn>
                    <GridColumn
                        colSpan={{ sm: 4, md: 6, lg: 3 }}
                        className={styles[`bio-reviews__left`]}
                    >
                        {typeof overallRating === 'number' && overallRating > 0 && (
                            <>
                                <Text
                                    tag="div"
                                    variant={Text.VARIANT.E1}
                                    content={'OVERALL RATING'}
                                    className={styles[`bio-reviews__rating-label`]}
                                />
                                <div className={styles['bio-reviews__rating-wrapper']}>
                                    <Title
                                        tag="h3"
                                        variant={Text.VARIANT.H3}
                                        content={overallRating.toFixed(1)}
                                        className={styles[`bio-reviews__rating-score`]}
                                    />
                                    <div className={styles[`bio-reviews__rating-stars`]}>
                                        <StarRatings total={5} ratings={overallRating} />
                                    </div>
                                    {numberOfRate > 0 && (
                                        <Text
                                            tag="div"
                                            variant={Text.VARIANT.T3}
                                            content={`(${numberOfRate} ratings)`}
                                            className={styles[`bio-reviews__rating-count`]}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </GridColumn>
                    <GridColumn
                        colSpan={{ sm: 4, md: 6, lg: 9 }}
                        className={styles[`bio-reviews__right`]}
                    >
                        {reviews?.length > 0 && (
                            <>
                                <Toolbar
                                    numberOfReviews={reviews?.length}
                                    selectedKey={sortBy}
                                    onSelectionChange={handleSelectionChange}
                                    dropdownRef={dropdownRef}
                                />
                                <ul className={styles[`bio-reviews__list`]}>
                                    {currentReviews?.map((review, index) => (
                                        <li
                                            className={styles[`bio-reviews__list-item`]}
                                            key={`review-${index}`}
                                            ref={
                                                index === firstNewReviewIndex
                                                    ? firstNewReviewRef
                                                    : undefined
                                            }
                                            tabIndex={
                                                index > 0 && index === firstNewReviewIndex
                                                    ? 0
                                                    : undefined
                                            }
                                        >
                                            <Review {...review} />
                                        </li>
                                    ))}
                                </ul>
                                <div
                                    className={styles[`bio-reviews__load-more-container`]}
                                    ref={loadMoreRef}
                                >
                                    {currentReviews.length < reviews.length && (
                                        <Button
                                            label={'More Results'}
                                            ariaLabel={'Load More Results'}
                                            buttonStyle={Button.STYLE.SECONDARY}
                                            data-trigger="button"
                                            onClick={handleLoadMoreClick}
                                            className={styles[`bio-reviews__load-more`]}
                                            analytics={{
                                                componentName: 'Fad Bio Reviews',
                                                componentTitle: title || '',
                                                actionLabel: 'More Results',
                                                interactionType: 'fad_bio_cta',
                                                selector: 'button',
                                                providerRating: overallRating || '',
                                            }}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </GridColumn>
                </Grid>
            </ContentContainer>
        </section>
    );
};

BioReviews.propTypes = {
    /**
     * title
     */
    title: PropTypes.string,
    /**
     * id for Deep Link
     */
    htmlId: PropTypes.string,
    /**
     * html content of tooltip
     */
    toolTipContent: PropTypes.string,
    /**
     * number of rates
     */
    numberOfRate: PropTypes.number,
    /**
     * overall rating
     */
    overallRating: PropTypes.number,
    /**
     * reviews array
     */
    reviews: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            surveyKey: PropTypes.number,
            source: PropTypes.string,
            mentionTime: PropTypes.number,
            wordCount: PropTypes.number,
            comment: PropTypes.string,
            overallRating: PropTypes.shape({
                name: PropTypes.string,
                value: PropTypes.number,
            }),
        }),
    ),
};

/**
 * BioReview default props.
 * @type {Object}
 * @property {string} title
 */
BioReviews.defaultProps = {
    title: 'Patient Reviews',
    htmlId: 'reviews',
};
export default BioReviews;
